-- | This module implements a generic search widget for filtering items by
-- | teacher, semester, class, and keywords.
module Widgets.Search where

import Common

import API.Search (SearchClass, SearchData, SearchQuery, SearchSemester, SearchTeacher, classSearchAff, semesterSearchAff, teacherSearchAff)
import Concur.Core (Widget)
import Concur.React (HTML)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Maybe (Maybe(..), isJust)
import Data.String as Str
import Widgets.BetterSelect (Select, SelectConfig, SelectOption, defSelectConfig, initSelect', preloadSelect, selectWidget, selectedItem, setSelected, wrapSelectOption')


-- | Tracks the state of the current selection.
-- |
-- | This acts as the state and return value of the `searchWidget` by
-- | containing the user's current query.
type SearchState =
    { teacher :: Select SearchTeacher
    , semester :: Select SearchSemester
    , class :: Select SearchClass
    , keyword :: String
    }

hasFiltersSet :: SearchState -> Boolean
hasFiltersSet {teacher, semester, class: class', keyword} =
    (isJust $ selectedItem $ teacher) ||
    (isJust $ selectedItem $ semester) ||
    (isJust $ selectedItem $ class') ||
    (Str.length keyword > 0)

-- | Load search state from initial search data.
loadSearchState :: SearchData -> SearchState
loadSearchState { teachers, semesters, classes, defaults: { teacher, semester, group } } = init
    { teacher = setSelected (preloadSelect init.teacher teachers') $
                  (\v -> { label: v.name, value: v }) <$> teacher
    , semester = setSelected (preloadSelect init.semester semesters') $
                  (\v -> { label: v.name, value: v }) <$> semester
    , class = setSelected (preloadSelect init.class classes') $
                  (\v -> { label: v.name, value: v }) <$> group
    }
  where
    init = initSearchState
    teachers' :: Array (SelectOption SearchTeacher)
    teachers' = wrapSelectOption' _.name teachers
    semesters' :: Array (SelectOption SearchSemester)
    semesters' = wrapSelectOption' _.name semesters
    classes' :: Array (SelectOption SearchClass)
    classes' = wrapSelectOption' _.name classes

-- | Initial search state with given list of teachers and semesters, but
-- | nothing selected.
initSearchState :: SearchState
initSearchState =
    { teacher: initSelect' "teacher" teacherSearchAff teacherSelectConfig
    , semester: initSelect' "semester" semesterSearchAff semesterSelectConfig
    , class: initSelect' "class" classSearchAff classSelectConfig
    , keyword: ""
    }

teacherSelectConfig :: SelectConfig
teacherSelectConfig = defSelectConfig
    { placeholder = Just $ lookupTranslation "search-teacher"
    , deselectLabel = Just $ lookupTranslation "search-teacher-all"
    }

semesterSelectConfig :: SelectConfig
semesterSelectConfig = defSelectConfig
    { placeholder = Just $ lookupTranslation "search-semester"
    , deselectLabel = Just $ lookupTranslation "search-semester-all"
    }

classSelectConfig :: SelectConfig
classSelectConfig = defSelectConfig
    { placeholder = Just $ lookupTranslation "search-class"
    , deselectLabel = Just $ lookupTranslation "search-class-all"
    }


-- | Gets the currently selected teacher from the given `SearchState`.
selectedTeacher :: SearchState -> Maybe SearchTeacher
selectedTeacher = map _.value <<< selectedItem <<< _.teacher

-- | Gets the currently selected semester from the given `SearchState`.
selectedSemester :: SearchState -> Maybe SearchSemester
selectedSemester = map _.value <<< selectedItem <<< _.semester

-- | Gets the currently selected semester from the given `SearchState`.
selectedClass :: SearchState -> Maybe SearchClass
selectedClass = map _.value <<< selectedItem <<< _.class


-- | Turns the given search state into a `SearchQuery` for the API.
stateToQuery :: SearchState -> SearchQuery
stateToQuery state =
    { teacher: map (_.id) $ selectedTeacher state
    , class: map (_.id) $ selectedClass state
    , semester: map (_.semester) $ selectedSemester state
    , search: if state.keyword /= "" then Just state.keyword else Nothing
    }

-- | Renders the actual search widget with a given state.
-- |
-- | When a selection is changed, the state is updated and new state is
-- | returned. The new state can then be queried for the current selections.
searchWidget :: SearchState -> Widget HTML SearchState
searchWidget state = do
    D.div [ P.className "search-form" ]
        [ D.div [ P.className "search-selects" ]
            [ D.div
                [ P.className "teacher-search-wrapper" ]
                [ D.label
                    [ P.className "teacher-search-label"
                    , P.htmlFor "teacher"
                    ]
                    [ D.text $ lookupTranslation "teacher" ]
                , state { teacher = _ } <$> selectWidget state.teacher
                ]
            , D.div
                [ P.className "semester-search-wrapper" ]
                [ D.label
                    [ P.className "semester-search-label"
                    , P.htmlFor "semester"
                    ]
                    [ D.text $ lookupTranslation "semester" ]
                , state { semester = _ } <$> selectWidget state.semester
                ]
            , D.div
                [ P.className "class-search-wrapper" ]
                [ D.label
                    [ P.className "class-search-label"
                    , P.htmlFor "class"
                    ]
                    [ D.text $ lookupTranslation "class" ]
                , state { class = _ } <$> selectWidget state.class
                ]
            ]
        , state { keyword = _ } <$> searchInput state.keyword
        ]

searchInput :: String -> Widget HTML String
searchInput q =  D.input
    [ P._type "text"
    , P.className "form-control search widget-input"
    , P.value q
    , P.unsafeTargetValue <$> P.onInput
    , P.key "search-search-input"
    , P.placeholder $ lookupTranslation "search"
    ]
